import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Image } from 'react-bootstrap';
import classNames from 'classnames';
import { Typography } from 'Components/atoms';
import { AND, ENGAGEMENT_EDIT, MORE } from 'Core-utils/constants/constants';
import { AllowedExpandableEngagementColors, AutoCompleteItem } from 'Core-utils/types';
import './styles.scss';

interface ExpandableMenuCardProps {
    header: string;
    caption: (AutoCompleteItem | string)[];
    defaultCaption?: string;
    children: JSX.Element;
    color: AllowedExpandableEngagementColors;
    onSave?: (caption: (AutoCompleteItem | string)[]) => void;
    mode?: 'default' | 'locked';
    /** inline: comma-separated chips (default). stacked: one caption row per item. */
    captionLayout?: 'inline' | 'stacked';
    /** When set, replaces default caption while collapsed. */
    collapsedCaption?: ReactNode;
}

const ExpandableMenuCard = ({
    header,
    caption,
    children,
    color,
    onSave,
    defaultCaption,
    mode = 'default',
    captionLayout = 'inline',
    collapsedCaption,
}: ExpandableMenuCardProps) => {
    const accordionRef = useRef(null);
    const isLocked = mode === 'locked';
    const [isOpen, setIsOpen] = useState(isLocked);
    const isEditing = isLocked || isOpen;

    const filteredCaption = caption.filter((item) => item !== null && item !== undefined);

    const toggleEditingState = () => {
        if (isLocked) {
            return;
        }
        setIsOpen((open) => !open);
    };

    const handleOutsideClick = (event: MouseEvent) => {
        if (accordionRef.current && !(accordionRef.current as HTMLDivElement).contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isLocked) {
            return;
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
            caption.length > 0 && onSave && onSave(caption);
        }
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isOpen, isLocked]);

    const containerClasses = classNames('expandable-engagement w-100 position-relative', { open: isEditing });
    const headerClasses = classNames(`expandable-engagement__accordion-header bg-${color}`, { editing: !isEditing });

    const buttonText = !isEditing && ENGAGEMENT_EDIT;

    const renderDefaultCaption = () => (
        <Typography variant={'body5'} color="text-medium-emphasis" style={{ marginTop: '0.5rem' }}>
            {defaultCaption}
        </Typography>
    );

    const isStackedCaption = captionLayout === 'stacked';

    const renderCaptionItem = (item: AutoCompleteItem, index: number, arrayLength: number) => {
        const providerIcon =
            typeof item?.icon === 'string' ? <Image src={item.icon} width={15} height={17} /> : item?.icon;
        return (
            <div
                key={index}
                className={classNames('d-flex gap-1 align-items-center mt-2', {
                    'justify-content-center': !isStackedCaption,
                    'justify-content-start': isStackedCaption,
                })}
            >
                {item?.icon && providerIcon}
                <Typography variant="body5">{item.value}</Typography>
                {!isStackedCaption && index < arrayLength - 1 && <span>,</span>}
            </div>
        );
    };

    const filteredCaptionItems = filteredCaption as AutoCompleteItem[];

    const renderCaption = () => (
        <>
            {filteredCaptionItems.length === 0 && renderDefaultCaption()}
            <div
                className={classNames('d-flex gap-1 parent-container', {
                    'flex-column align-items-start': isStackedCaption,
                    'justify-content-between align-items-center': !isStackedCaption,
                })}
            >
                {filteredCaptionItems
                    .slice(0, 4)
                    .map((item, index) => renderCaptionItem(item, index, filteredCaptionItems.length))}
                {filteredCaptionItems.length > 4 && (
                    <Typography variant="body5" style={{ marginTop: '0.5rem' }}>
                        {`${AND} ${filteredCaptionItems.length - 4} ${MORE}`}
                    </Typography>
                )}
            </div>
        </>
    );

    return (
        <div className={containerClasses} data-testid="expandable-engagement" ref={accordionRef}>
            <div className={headerClasses}>
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className="d-flex flex-column align-items-start">
                        <Typography variant="h6">{header}</Typography>
                        {!isEditing && (collapsedCaption ?? renderCaption())}
                    </div>
                    {!isLocked && (
                        <Typography variant="body1">
                            <u onClick={toggleEditingState}>{buttonText}</u>
                        </Typography>
                    )}
                </div>
            </div>
            <div className={`expandable-engagement__accordion-content bg-${color}`}>{isEditing && children}</div>
            <div className={`expandable-engagement__bulge ${color}`} />
        </div>
    );
};
export default ExpandableMenuCard;
